import type { Maybe } from 'graphql/jsutils/Maybe';
import type { ReactElement } from 'react';

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ChildrenPanel } from './ChildrenPanel';
import { ParentNodes } from './ParentNodes';
import { media } from '../../shared/core/styles';
import type { InpageNavProps } from '~/types/props';
import type { NavigationItem } from '~/utilities/graphql/codegen';

export type NodeHeightInterface = {
  parentHeight: number;
  childrenHeights: { [k: number]: number };
};

export const getChildrenPanelHeight = (
  height: NodeHeightInterface | undefined,
  openChildIndex: number | undefined
): number => {
  if (!height) {
    return 0;
  }

  if (openChildIndex === undefined) {
    return height.parentHeight;
  }

  return height.parentHeight + height.childrenHeights[openChildIndex];
};

const S = {
  Wrapper: styled.section`
    visibility: visible;
    opacity: 1;
    height: auto;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      visibility: hidden;
      opacity: 0;
      height: 0;
      border: none;
    }
  `,

  Nodes: styled.div`
    background-color: ${({ theme }) => theme.colors.background.base};
    min-height: 53px;
    border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
    display: grid;
    grid-template-columns: 1fr repeat(2, 50%) 1fr;
  `,
};

export const MobileCategory = ({ inpageNavigation }: InpageNavProps): ReactElement => {
  const [parentNode, setParentNode] = useState<{ [k: number]: boolean }>({});
  const [childrenNodeIndex, setChildrenNodeIndex] = useState<{ [k: number]: number | undefined }>(
    {}
  );
  const [heights, setHeights] = useState<NodeHeightInterface[]>([]);
  const parentRef = useRef<Array<HTMLDivElement | null>>([]);
  const childrenRef = useRef<
    Array<{
      [k: number]: HTMLDivElement | null;
    }>
  >([]);

  useEffect(() => {
    if (parentRef.current && childrenRef) {
      const parentHeights = parentRef.current.map(node => node?.scrollHeight || 0);
      const nodeHeights = childrenRef.current.map((child, i) => {
        let childrenHeights = {};

        Object.entries(child).forEach(([key, element]) => {
          childrenHeights = {
            ...childrenHeights,
            [key]: element?.scrollHeight || 0,
          };
        });

        return {
          parentHeight: parentHeights[i],
          childrenHeights,
        };
      });

      setHeights(nodeHeights);
    }
  }, []);

  if (!inpageNavigation?.navigation) {
    return <></>;
  }

  const childNodes: (Maybe<NavigationItem>[] | undefined)[] = [];

  inpageNavigation.navigation.forEach(node => {
    childNodes.push(node?.children?.filter(childNode => childNode?.children));
  });

  const handleParentNodeClick = (i: number) => {
    if (Object.values(parentNode).some(isOpen => !!isOpen)) {
      setParentNode({});
      setTimeout(() => {
        setParentNode({ [i]: !parentNode[i] });
      }, 500);
    } else {
      setParentNode({ [i]: !parentNode[i] });
    }
  };

  const handleChildLabelClick = (i: number, j: number) => {
    setChildrenNodeIndex(prev =>
      childrenNodeIndex[i] === j ? { ...prev, [i]: undefined } : { ...prev, [i]: j }
    );
  };

  return (
    <S.Wrapper>
      <nav>
        <S.Nodes role="tablist">
          <ParentNodes
            navigation={inpageNavigation.navigation}
            parentNode={parentNode}
            onParentNodeClick={handleParentNodeClick}
          />
        </S.Nodes>
        {childNodes.map((childNode, i) => (
          <ChildrenPanel
            key={inpageNavigation.navigation?.[i]?.uid ?? i}
            childNode={childNode}
            i={i}
            isOpen={!!parentNode[i]}
            openChildIndex={childrenNodeIndex[i]}
            height={getChildrenPanelHeight(heights[i], childrenNodeIndex[i])}
            childrenHeights={heights[i]?.childrenHeights ?? {}}
            onParentRef={el => {
              parentRef.current[i] = el;
            }}
            onChildRef={(el, j) => {
              childrenRef.current[i] = { ...childrenRef.current[i], [j]: el };
            }}
            onChildLabelClick={j => handleChildLabelClick(i, j)}
          />
        ))}
      </nav>
    </S.Wrapper>
  );
};
